<template>
  <div>
    <select :value="value.qid" @change="handleQuestionChange">
      <option v-if="origin_index === 0" value="">{{ parseTranslation(NONE) }}</option>

      <option
        v-for="option in options"
        :key="`${id}-${option.value}`"
        :value="option.value"
        >{{ option.label }}</option
      >
    </select>

    <select
      :value="value.target"
      :disabled="!referencedQuestionHasSubquestions"
      @change="handleTargetChange"
    >
      <option value="" disabled>{{ parseTranslation(TARGET) }}</option>
      <option value="answers">{{ parseTranslation(ANSWERS) }}</option>
      <option value="subQuestions">{{ parseTranslation(SUBQUESTIONS) }}</option>
    </select>

    <select :value="value.type" @change="handleTypeChange">
      <option value="selected">{{ parseTranslation(SELECTED) }}</option>
      <option value="not_selected">{{ parseTranslation(UNSELECTED) }}</option>
    </select>

    <select :value="value.alternative" @change="handleAlternativeReferenceChange">
      <option
        v-for="(alternativeReference, index) in alternativeReferences"
        :value="alternativeReference.value"
        :key="`${id}-alternative-reference-${index}`"
      >
        {{ alternativeReference.label }}
      </option>
    </select>

    <button class="remove-btn" @click="removeSelf">
      <DeleteIcon />
    </button>

    <button v-if="canAddMoreOrigins" class="add-btn" @click="addNewOrigin">
      +
    </button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DeleteIcon from '@/components/imgs/Close'

import { questionsConfig } from '@/utils/questions'
import { getLastIndex, earlyExitReduce } from '@/utils/array'

import { acceptedTypes, singleReferenceTypes } from './contants'

import * as translationInput from './translations'
const { default: translations, ...translationKeys } = translationInput

const FIRST_ITEM = 0

export default {
  name: 'SingleOrigin',

  components: {
    DeleteIcon,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
    question_index: {
      required: true,
      type: Number,
    },
    questions_groups_index: {
      required: true,
      type: Number,
    },
    origin_index: {
      required: true,
      type: Number,
    },
  },

  data() {
    return {
      translations,
      ...translationKeys,
      FIRST_ITEM,
      advancedOption: 'advanced_inheritance',
    }
  },

  watch: {
    referenceQuestion() {
      if (!this.referenceQuestion || this.referenceQuestion[this.advancedOption])
        return this.removeSelf()

      this.validateCodeChange()
      this.validateAlternativeCodeChange()
    },
  },

  computed: {
    ...mapGetters(['questionGroups']),

    allPreviousQuestions() {
      const priorQuestions = this.questionGroups
        .slice(0, this.questions_groups_index)
        .map(({ questions }) => questions)
        .flat()
        .filter(
          ({ type, advancedOptions }) =>
            acceptedTypes.includes(type) && !advancedOptions[this.advancedOption],
        )

      const actualGroupQuestions = this.questionGroups[
        this.questions_groups_index
      ].questions.filter(
        ({ type, advancedOptions }, index) =>
          index < this.question_index &&
          acceptedTypes.includes(type) &&
          !advancedOptions[this.advancedOption],
      )

      return [...priorQuestions, ...actualGroupQuestions]
    },

    referenceQuestion() {
      if (!this.value.qid) return null

      const question = this.allPreviousQuestions.find(({ qid }) => qid === this.value.qid)

      return question
    },

    referencedQuestionHasSubquestions() {
      return questionsConfig[this.referenceQuestion?.type]?.hasSubQuestion || false
    },

    fullConfig() {
      return this.questionGroups[this.questions_groups_index].questions[
        this.question_index
      ].advancedOptions[this.advancedOption]
    },

    value() {
      return this.questionGroups[this.questions_groups_index].questions[
        this.question_index
      ].advancedOptions[this.advancedOption].origin[this.origin_index]
    },

    options() {
      const questionOptions = this.allPreviousQuestions.filter(question => {
        const { qid, type } = question

        const currentRef = this.referenceQuestion.qid === qid

        if (currentRef) return true

        const priorReferences = this.fullConfig.origin.filter(
          origin => origin.qid === qid,
        )

        if (singleReferenceTypes.includes(type)) return !priorReferences.length

        const allReferenced = priorReferences.find(({ alternative }) => !alternative)

        if (allReferenced) return false

        const allAlternativesReferenced =
          priorReferences.length === question[this.value.target].length

        return !allAlternativesReferenced
      })

      return questionOptions.map(({ qid, code }) => ({ value: qid, label: code }))
    },

    canAddMoreOrigins() {
      const isLastIndex = this.origin_index === getLastIndex(this.fullConfig.origin)

      if (!isLastIndex) return false

      const total = earlyExitReduce(
        this.options,
        (acc, next) => {
          const question = this.allPreviousQuestions.find(({ qid }) => qid === next.value)

          const maxAlternatives = Math.max(
            ...['answers', 'subQuestions'].map(
              prop => this.getAlternativeOptions(question, prop, true).length,
            ),
          )

          return acc + maxAlternatives
        },
        totalOptions => totalOptions > this.fullConfig.origin.length,
        0,
      )

      return total > this.fullConfig.origin.length
    },

    alternativeReferences() {
      if (!this.referenceQuestion) return []

      return this.getAlternativeOptions(this.referenceQuestion)
    },
  },

  methods: {
    getLastIndex,

    validateCodeChange() {
      const { code } = this.referenceQuestion

      const codeChanged = this.value.code !== code

      if (!codeChanged) return

      this.updateSingleOrigin({ ...this.value, code })
    },

    validateAlternativeCodeChange() {
      const refIsSubQuestions = this.value.target === 'subQuestions'
      const invertedRef = refIsSubQuestions ? 'answers' : 'subQuestions'

      const shouldInvert =
        refIsSubQuestions || questionsConfig[this.referenceQuestion.type].hasSubQuestion

      const alternativeKey = shouldInvert ? invertedRef : this.value.target

      const alternatives = this.referenceQuestion[alternativeKey]

      const referencedAlternative = alternatives.find(
        ({ code }) => `${code}` === `${this.value.alternative}`,
      )

      if (referencedAlternative || this.value.alternative === null) return

      this.removeSelf()
    },

    executeOptionUpdate(option) {
      this.$store.dispatch('changeAdvanced', {
        questionGroupIndex: this.questions_groups_index,
        questionIndex: this.question_index,
        advancedKey: this.advancedOption,
        value: option,
      })
    },

    removeOption() {
      this.executeOptionUpdate(null)
    },

    removeSelf() {
      const updatedOrigin = this.fullConfig.origin.filter(
        (_, originIndex) => originIndex !== this.origin_index,
      )

      if (!updatedOrigin.length) return this.removeOption()

      this.executeOptionUpdate({
        ...this.fullConfig,
        origin: updatedOrigin,
      })
    },

    updateSingleOrigin(updated) {
      const updatedOrigin = this.fullConfig.origin.map((origin, originIndex) =>
        originIndex !== this.origin_index ? origin : updated,
      )

      this.executeOptionUpdate({
        ...this.fullConfig,
        origin: updatedOrigin,
      })
    },

    createSingleOrigin(newOrigin) {
      this.executeOptionUpdate({
        ...this.fullConfig,
        origin: [...this.fullConfig.origin, newOrigin],
      })
    },

    handleAlternativeReferenceChange({ target: { value } }) {
      this.updateSingleOrigin({ ...this.value, alternative: `${value}` || null })
    },

    findSelectedOrigin({ alternative, qid, type, target, ignoreIndex }) {
      return this.fullConfig.origin.find((origin, originIndex) => {
        // eslint-disable-next-line prettier/prettier
        const alternativeComparison  = alternative ? alternative === origin.alternative : true
        const qidComparison = qid === origin.qid
        const typeComparison = type ? origin.type === type : true
        const targetComparison = target ? origin.target === target : true

        // eslint-disable-next-line prettier/prettier
        const indexComparison = typeof ignoreIndex !== 'undefined' ? ignoreIndex !== originIndex : true

        return [
          alternativeComparison,
          qidComparison,
          indexComparison,
          typeComparison,
          targetComparison,
        ].every(Boolean)
      })
    },

    handleTargetChange({ target: { value } }) {
      const newAlternatives = this.getAlternativeOptions(this.referenceQuestion, value)

      if (!newAlternatives.length) return this.removeSelf()

      const currentAlternativeRemainsValid = newAlternatives.find(
        ({ value }) => value === this.value.alternative,
      )

      if (currentAlternativeRemainsValid)
        return this.updateSingleOrigin({
          ...this.value,
          target: value,
        })

      const notSelectedAlternative = newAlternatives.find(
        ({ value }) =>
          !this.findSelectedOrigin({
            alternative: value,
            qid: this.value.qid,
            ignoreIndex: this.origin_index,
          }),
      )

      if (!notSelectedAlternative) return this.removeSelf()

      this.updateSingleOrigin({
        ...this.value,
        target: value,
        alternative: notSelectedAlternative.value,
      })
    },

    handleQuestionChange({ target: { value } }) {
      if (!value) return this.removeOption()

      const newQuestion = this.allPreviousQuestions.find(({ qid }) => qid === value)

      const updatedOrigin = {
        ...this.value,
        qid: value,
        code: newQuestion.code,
        target: 'answers',
        alternative: null,
      }

      this.updateSingleOrigin(updatedOrigin)
    },

    handleTypeChange({ target: { value } }) {
      this.updateSingleOrigin({ ...this.value, type: value })
    },

    getInheritOrigin(question) {
      const {
        advancedOptions: { array_filter, array_filter_reverse },
      } = question

      const inheritCode = array_filter || array_filter_reverse

      if (!inheritCode) return question

      const refQuestion = this.allPreviousQuestions.find(
        ({ code }) => inheritCode === code,
      )

      if (!refQuestion) return question

      return this.getInheritOrigin(refQuestion)
    },

    getAlternativeOptions(question, target, log = false) {
      const all = { value: null, label: 'all' }

      if (singleReferenceTypes.includes(question.type)) return [all]

      // we want to inherit the inverse
      const actualTarget = target || this.value.target
      const refIsSubQuestions = actualTarget === 'subQuestions'
      const markedReference = refIsSubQuestions ? 'answers' : 'subQuestions'

      const shouldGetInheritOrigin =
        refIsSubQuestions &&
        ['array_filter', 'array_filter_reverse'].some(inherit => question[inherit])

      const actualQuestion = shouldGetInheritOrigin
        ? this.getInheritOrigin(question)
        : question

      const options = actualQuestion[markedReference].map(({ code, label }) => ({
        value: code,
        // eslint-disable-next-line prettier/prettier
        label: `${actualTarget} ${refIsSubQuestions ? 'in column' : 'in'} "${label}"`,
      }))

      return [all, ...options]
    },

    addNewOrigin() {
      const [{ value }] = this.options.slice().reverse()

      const question = this.allPreviousQuestions.find(({ qid }) => qid === value)

      const alternativeOptions = this.getAlternativeOptions(question)

      const alternativeNotSelected = alternativeOptions.find(
        alternativeOption =>
          !this.fullConfig.origin.find(
            ({ qid, alternative }) => qid === value && alternativeOption === alternative,
          ),
      )

      const newOption = {
        qid: value,
        code: question.code,
        target: 'answers',
        type: 'selected',
        alternative: alternativeNotSelected.value,
      }

      this.createSingleOrigin(newOption)
    },
  },
}
</script>

<style scoped lang="scss">
span {
  color: red;
}

select {
  @include advancedOptionSelect;
  max-width: 450px;

  & + select {
    margin-left: 12px;
  }
}

.add-btn {
  @include tipography(20);
  @include boxSizing(22);
  @include flexMiddle;
  display: inline-flex;

  margin-left: 6px;
  background: $blue;
  border: none;
  border-radius: 50%;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: $blue-2;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    @include disabled-button;
  }
}

.remove-btn {
  @include transparentButton;
  display: inline-flex;
  @include boxSizing(18);
  margin-left: 6px;

  svg {
    @include boxSizing(12);
  }

  .light & {
    color: #fff;
  }
}
</style>
